<template>
  <div class="orders-page">
    <AppNavbar />

    <main class="container orders-content">
      <div class="page-head">
        <div class="page-head-left">
          <h1 class="page-title">My Orders</h1>
          <p class="page-sub" v-if="!isLoading && orders.length">
            {{ orders.length }} order{{ orders.length !== 1 ? "s" : "" }} found
          </p>
        </div>
      </div>

      <!-- FILTERS -->
      <div class="filter-row">
        <button
          v-for="f in filters"
          :key="f.value"
          type="button"
          class="filter-chip"
          :class="{ active: activeFilter === f.value }"
          @click="selectFilter(f.value)"
        >
          {{ f.label }}
        </button>
      </div>

      <!-- STATES -->
      <p v-if="isLoading" class="status-text">Loading your orders...</p>
      <p v-else-if="errorMessage" class="status-text error">
        {{ errorMessage }}
      </p>

      <div v-else-if="!filteredOrders.length" class="empty-state">
        <div class="empty-icon-wrap">
          <i class="ri-shopping-bag-line"></i>
        </div>
        <p class="empty-title">No orders yet</p>
        <p class="empty-sub">Your placed orders will show up here.</p>
      </div>

      <!-- ORDERS LIST -->
      <div v-else class="orders-list">
        <div
          v-for="(order, orderIndex) in filteredOrders"
          :key="order.orderId || orderIndex"
          class="order-card"
        >
          <!-- HEADER -->
          <div class="order-head">
            <div class="order-id-group">
              <div class="order-id-row">
                <span class="order-id">#{{ order.orderId }}</span>
                <span
                  class="status-pill"
                  :class="statusPillClass(order.orderStatus)"
                >
                  {{ displayStatus(order.orderStatus) }}
                </span>
              </div>
              <span class="order-date">
                <i class="ri-calendar-line"></i>
                {{ formatDate(order.orderDateTime) }}
                <!-- <span v-if="order.storeName"
                  >&nbsp;·&nbsp;{{ order.storeName }}</span
                > -->
              </span>
              <!-- <span v-if="order.billId" class="order-date">
                <i class="ri-receipt-line"></i>
                Bill: {{ order.billId }}
              </span> -->
            </div>
            <div class="order-amount-col">
              <span class="order-amount">{{
                formatCurrency(order.orderAmount)
              }}</span>
              <span class="order-amount-label">Total</span>
            </div>
          </div>

          <!-- ITEMS -->
          <div class="items-area">
            <div
              v-for="(item, index) in order.orderItems"
              :key="item.itemCode || item.itemName || index"
              class="item-block"
            >
              <div class="item-row">
                <div class="item-thumb">
                  <i class="ri-shopping-bag-line"></i>
                </div>
                <div class="item-info">
                  <p class="item-name">
                    <span
                      :ref="
                        (el) =>
                          registerItemNameElement(
                            getItemNameToggleKey(
                              order,
                              orderIndex,
                              item,
                              index,
                            ),
                            el,
                          )
                      "
                      :class="[
                        'item-name-text',
                        isItemNameExpanded(
                          getItemNameToggleKey(order, orderIndex, item, index),
                        )
                          ? 'item-name-text--expanded'
                          : 'item-name-text--collapsed',
                      ]"
                    >
                      {{ getItemDisplayName(item) }}
                    </span>
                  </p>
                  <button
                    v-if="shouldShowReadMore(order, orderIndex, item, index)"
                    type="button"
                    class="item-read-more-btn"
                    @click="
                      toggleItemName(
                        getItemNameToggleKey(order, orderIndex, item, index),
                      )
                    "
                  >
                    {{
                      isItemNameExpanded(
                        getItemNameToggleKey(order, orderIndex, item, index),
                      )
                        ? "Read less"
                        : "Read more"
                    }}
                  </button>
                  <p class="item-sub">
                    Qty:
                    {{ item.itemQuantity }}
                  </p>
                </div>
                <!-- <span class="tracking-stage-hint">
                  {{ stageName(resolveStage(order.orderStatus)) }}
                </span> -->
                <!-- <span class="item-amt">{{ formatCurrency(item.itemPrice) }}</span> -->
              </div>

              <!-- <div
                v-if="resolveStage(order.orderStatus) > 0"
                class="item-tracking-block"
              >
                <button
                  type="button"
                  class="tracking-toggle"
                  @click="
                    toggleTracking(
                      getTrackingKey(order, orderIndex, item, index),
                    )
                  "
                >
                  <div class="tracking-toggle-left">
                    <div class="tracking-icon-dot">
                      <i class="ri-map-pin-line"></i>
                    </div>
                    <span class="tracking-label">
                      {{ item.itemName || "Product" }} tracking
                    </span>
                  </div>
                  <div class="tracking-toggle-right">
                    <span class="tracking-stage-hint">
                      {{ stageName(resolveStage(order.orderStatus)) }}
                    </span>
                    <i
                      class="ri-arrow-down-s-line toggle-chevron"
                      :class="{
                        rotated: expandedTracking.has(
                          getTrackingKey(order, orderIndex, item, index),
                        ),
                      }"
                    ></i>
                  </div>
                </button>

                <div
                  v-if="
                    expandedTracking.has(
                      getTrackingKey(order, orderIndex, item, index),
                    )
                  "
                  class="track-body"
                >
                  <div class="track">
                    <div
                      v-for="(step, i) in trackingSteps"
                      :key="step.key"
                      class="track-step-group"
                    >
                      <div class="t-step">
                        <div
                          class="t-icon-wrap"
                          :class="stepIconClass(order.orderStatus, i)"
                        >
                          <i :class="step.icon"></i>
                        </div>
                        <span
                          class="t-name"
                          :class="stepIconClass(order.orderStatus, i)"
                        >
                          {{ step.label }}
                        </span>
                      </div>
                      <div
                        v-if="i < trackingSteps.length - 1"
                        class="t-line"
                        :class="lineClass(order.orderStatus, i)"
                      ></div>
                    </div>
                  </div>
                </div>
              </div> -->
            </div>

            <p v-if="!order.orderItems.length" class="status-text">
              {{
                order.itemsCount ? `${order.itemsCount} item(s)` : "No items"
              }}
            </p>
          </div>

          <!-- FOOTER — receipt link + delivery status + (optional) allocate CV -->
          <div class="order-footer">
            <div class="order-footer-left">
              <a
                v-if="
                  isSuccessStatus(order.orderStatus) &&
                  hasValidReceiptUrl(order.digitalReceiptUrl)
                "
                :href="order.digitalReceiptUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="receipt-link"
              >
                <i class="ri-external-link-line"></i> View digital receipt
              </a>
              <button
                type="button"
                class="track-delivery-btn"
                @click="goToDeliveryStatus(order)"
              >
                <i class="ri-truck-line"></i> Delivery Status
              </button>
            </div>
            <button
              v-if="showCv && isSuccessStatus(order.orderStatus)"
              type="button"
              class="allocate-btn receipt-allocate-btn"
              @click="openAllocateCvPopup(order)"
            >
              <i class="ri-flow-chart"></i> Allocate CV
            </button>
          </div>

          <div v-if="canContinueToPayment(order)" class="order-action-row">
            <button
              v-if="canContinueToPayment(order)"
              type="button"
              class="pay-now-btn"
              :disabled="processingOrderId === normalizeOrderId(order.orderId)"
              @click="checkPaymentStatusAndContinue(order)"
            >
              <!-- disabled -->
              <i
                :class="
                  processingOrderId === normalizeOrderId(order.orderId)
                    ? 'ri-loader-4-line spinning'
                    : 'ri-secure-payment-line'
                "
              ></i>
              {{
                processingOrderId === normalizeOrderId(order.orderId)
                  ? "Checking status..."
                  : "Check Payment Status"
              }}
            </button>
            <p
              v-if="
                paymentActionError &&
                paymentActionErrorOrderId === normalizeOrderId(order.orderId)
              "
              class="payment-action-error"
            >
              {{ paymentActionError }}
            </p>
          </div>
        </div>
        <p v-if="isLoadingMore" class="status-text">Loading more orders...</p>
      </div>
    </main>

    <div v-if="showPaymentFailurePopup" class="payment-popup-overlay">
      <div class="payment-popup-box" @keydown.enter.prevent>
        <button
          type="button"
          class="payment-popup-close"
          @click="closePaymentRetryFlow"
        >
          <i class="ri-close-line"></i>
        </button>
        <h3 class="payment-popup-title">Payment Not Successful</h3>
        <p class="payment-popup-sub">Your payment is not successful.</p>
        <div class="payment-popup-actions">
          <button
            type="button"
            class="payment-popup-btn primary"
            @click="openRetryPaymentOptionsPopup"
          >
            Retry Payment
          </button>
        </div>
      </div>
    </div>

    <div v-if="showRetryPaymentOptionsPopup" class="payment-popup-overlay">
      <div class="payment-popup-box" @keydown.enter.prevent>
        <button
          type="button"
          class="payment-popup-close"
          @click="closePaymentRetryFlow"
        >
          <i class="ri-close-line"></i>
        </button>
        <h3 class="payment-popup-title">Retry Payment</h3>
        <p class="payment-popup-sub">Choose a payment option to continue.</p>

        <div class="retry-options-wrap">
          <label
            class="retry-option-card"
            :class="{ active: retryPaymentGateway === 'hdfc' }"
          >
            <input
              v-model="retryPaymentGateway"
              type="radio"
              name="retry-payment-gateway"
              value="hdfc"
            />
            <span>HDFC</span>
          </label>
          <label
            class="retry-option-card"
            :class="{ active: retryPaymentGateway === 'freecharge' }"
          >
            <input
              v-model="retryPaymentGateway"
              type="radio"
              name="retry-payment-gateway"
              value="freecharge"
            />
            <span>Freecharge</span>
          </label>
        </div>

        <div class="payment-popup-actions">
          <button
            type="button"
            class="payment-popup-btn primary"
            :disabled="isRetryPaymentProcessing"
            @click="handleRetryPayment"
          >
            {{ isRetryPaymentProcessing ? "Processing..." : "Pay Now" }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showAllocateCvPopup" class="cv-popup-overlay">
      <div class="cv-popup-box" @keydown.enter.prevent>
        <button
          type="button"
          class="cv-popup-close"
          @click="showAllocateCvPopup = false"
        >
          <i class="ri-close-line"></i>
        </button>
        <iframe
          class="cv-popup-iframe"
          :src="allocateCvUrl"
          title="Allocate CV"
        ></iframe>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import * as ecomSdk from "rdep-ecom-sdk";
import { useRoute, useRouter } from "vue-router";
import AppNavbar from "../AppNavbar.vue";
import Footer from "../FooterView.vue";

const checkTransactionStatus = ecomSdk.checkTransactionStatus;
const getOrderList = ecomSdk.getOrderList;
const verifyPaymentStatus = ecomSdk.verifyStatus || null;
const initiateHdfcGatewayPayment =
  ecomSdk.initiateHdfcPayment ||
  ecomSdk.initiateCashfreePayment ||
  ecomSdk.initiateRazorPayPayment ||
  ecomSdk.generatePaymentLink;
const initiateFreechargeGatewayPayment =
  ecomSdk.initiateFreechargePayment ||
  ecomSdk.initiateFreeChargePayment ||
  ecomSdk.initiateAxisFreechargePayment;

const route = useRoute();
const router = useRouter();

const isLoading = ref(false);
const isLoadingMore = ref(false);
const errorMessage = ref("");
const orders = ref([]);
const nextCursorId = ref(null);
const hasMore = ref(true);
const bottomLoadTriggered = ref(false);
const activeFilter = ref("all");
const availableStatuses = ref([]);
// const expandedTracking = ref(new Set());
const processingOrderId = ref("");
const paymentActionError = ref("");
const paymentActionErrorOrderId = ref("");
const expandedItemNames = ref(new Set());
const itemNameElements = new Map();
const readMoreEligibleKeys = ref(new Set());
const showAllocateCvPopup = ref(false);
const allocateCvUrl = ref("");
const showPaymentFailurePopup = ref(false);
const showRetryPaymentOptionsPopup = ref(false);
const retryPaymentOrderId = ref("");
const retryPaymentUid = ref("");
const retryPaymentGateway = ref("hdfc");
const isRetryPaymentProcessing = ref(false);
const BA_URL = String(process.env.VUE_APP_BA_URL || "https://ba-ui.qa.rdep.io");

const PENDING_PAYMENT_ORDER_KEY = "payment.pendingOrderId";
const HDFC_CONTEXT_STORAGE_KEY = "hdfc.payment.context";

// const trackingSteps = [
//   { key: "placed", label: "Placed", icon: "ri-shopping-bag-line" },
//   { key: "transit", label: "In transit", icon: "ri-truck-line" },
//   { key: "delivered", label: "Delivered", icon: "ri-checkbox-circle-line" },
// ];

// ── TRACKING ─────────────────────────────────────────────────
// function resolveStage(orderStatus) {
//   const s = String(orderStatus || "").toUpperCase();
//   if (["DELIVERED", "COMPLETED", "COMPLETE", "SUCCESS"].includes(s)) return 2;
//   if (["SHIPPED", "IN_TRANSIT", "DISPATCHED", "OUT_FOR_DELIVERY"].includes(s))
//     return 1;
//   return 0;
// }

// function stageName(stage) {
//   return ["Order placed", "In transit", "Placed"][stage] ?? "Order placed";
// }

// function stepIconClass(orderStatus, i) {
//   const stage = resolveStage(orderStatus);
//   if (i < stage) return "done";
//   if (i === stage) return "active";
//   return "idle";
// }

// function lineClass(orderStatus, i) {
//   return i < resolveStage(orderStatus) ? "done" : "";
// }

// function toggleTracking(trackingKey) {
//   const next = new Set(expandedTracking.value);
//   next.has(trackingKey) ? next.delete(trackingKey) : next.add(trackingKey);
//   expandedTracking.value = next;
// }

// function getTrackingKey(order, orderIndex, item = null, itemIndex = 0) {
//   const orderId = String(order?.orderId || "").trim();
//   const itemName = String(
//     item?.itemName || order?.orderItems?.[itemIndex]?.itemName || "",
//   ).trim();

//   // Use both values together so duplicates in either field still get unique tracking identity.
//   if (orderId && itemName) return `${orderId}::${itemName}::${itemIndex}`;
//   if (orderId) return `order:${orderId}`;
//   if (itemName) return `item:${itemName}::${itemIndex}`;

//   return `order-${orderIndex}-item-${itemIndex}`;
// }

// ── NORMALIZE ────────────────────────────────────────────────
function normalizeOrders(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};
  const list = Array.isArray(response)
    ? response
    : Array.isArray(data.ordersData)
    ? data.ordersData
    : Array.isArray(nested.ordersData)
    ? nested.ordersData
    : Array.isArray(data.orders)
    ? data.orders
    : Array.isArray(nested.orders)
    ? nested.orders
    : Array.isArray(data.orderList)
    ? data.orderList
    : Array.isArray(nested.orderList)
    ? nested.orderList
    : Array.isArray(data.content)
    ? data.content
    : Array.isArray(nested.content)
    ? nested.content
    : [];

  if (!Array.isArray(list)) return [];

  const normalizeOrderItems = (order) => {
    const candidateItems =
      order?.orderItems ||
      order?.items ||
      order?.itemList ||
      order?.orderItemList ||
      order?.orderItemData ||
      order?.products ||
      order?.productList ||
      order?.orderDetails?.orderItems ||
      order?.data?.orderItems ||
      [];

    let items = Array.isArray(candidateItems) ? candidateItems : [];

    if (
      !items.length &&
      (order?.itemName || order?.itemCode || order?.itemSkuCode)
    ) {
      items = [order];
    }

    return items.map((item) => {
      const itemName =
        item?.itemName ||
        item?.item_name ||
        item?.name ||
        item?.productName ||
        item?.product_name ||
        item?.itemDescription ||
        item?.description ||
        "";

      const itemCode =
        item?.itemCode ||
        item?.item_code ||
        item?.itemSkuCode ||
        item?.skuCode ||
        item?.sku_code ||
        item?.productCode ||
        item?.product_code ||
        "";

      const itemPrice = Number(
        item?.itemPrice ?? item?.price ?? item?.amount ?? item?.lineAmount ?? 0,
      );

      return {
        ...item,
        itemName: String(itemName || "").trim(),
        itemCode: String(itemCode || "").trim(),
        itemPrice: Number.isFinite(itemPrice) ? itemPrice : 0,
      };
    });
  };

  const groupedOrders = new Map();

  for (const [index, o] of list.entries()) {
    const orderId = String(o?.orderId || "").trim();
    const fallbackKey = `row-${index}`;
    const groupKey = orderId || fallbackKey;
    const incomingItems = normalizeOrderItems(o);

    if (!groupedOrders.has(groupKey)) {
      groupedOrders.set(groupKey, {
        orderId: o.orderId,
        billId: o.billId,
        uid:
          o.uid ||
          o.paymentUid ||
          o.paymentUID ||
          o.merchantTxnId ||
          o.merchantTransactionId ||
          o.txnUid ||
          "",
        referenceId: o.referenceId || o.paymentReferenceId || "",
        transactionReferenceId:
          o.transactionReferenceId || o.transactionId || "",
        orderDateTime: o.orderDateTime,
        orderAmount: Number(o.orderAmount || 0),
        itemsCount: Number(o.itemsCount || 0),
        storeName: o.storeName,
        digitalReceiptUrl: o.digitalReceiptUrl,
        orderStatus: o.orderStatus,
        orderItems: incomingItems,
      });
      continue;
    }

    const existingOrder = groupedOrders.get(groupKey);
    existingOrder.orderItems.push(...incomingItems);

    existingOrder.itemsCount = Math.max(
      Number(existingOrder.itemsCount || 0),
      Number(o?.itemsCount || 0),
      existingOrder.orderItems.length,
    );

    if (!Number(existingOrder.orderAmount) && Number(o?.orderAmount || 0)) {
      existingOrder.orderAmount = Number(o.orderAmount || 0);
    }

    if (!existingOrder.storeName && o?.storeName) {
      existingOrder.storeName = o.storeName;
    }

    if (
      (!existingOrder.digitalReceiptUrl ||
        String(existingOrder.digitalReceiptUrl || "").endsWith("/null")) &&
      o?.digitalReceiptUrl
    ) {
      existingOrder.digitalReceiptUrl = o.digitalReceiptUrl;
    }

    if (!existingOrder.orderStatus && o?.orderStatus) {
      existingOrder.orderStatus = o.orderStatus;
    }

    if (!existingOrder.uid) {
      existingOrder.uid =
        o?.uid ||
        o?.paymentUid ||
        o?.paymentUID ||
        o?.merchantTxnId ||
        o?.merchantTransactionId ||
        o?.txnUid ||
        "";
    }

    if (!existingOrder.referenceId) {
      existingOrder.referenceId = o?.referenceId || o?.paymentReferenceId || "";
    }

    if (!existingOrder.transactionReferenceId) {
      existingOrder.transactionReferenceId =
        o?.transactionReferenceId || o?.transactionId || "";
    }
  }

  return Array.from(groupedOrders.values());
}

function getOrderPagination(response) {
  const root = response || {};
  const data = response?.data || response || {};
  const nested = data?.data || {};
  return {
    nextCursorId:
      root.nextCursorId ?? data.nextCursorId ?? nested.nextCursorId ?? null,
    hasMore: root.hasMore ?? data.hasMore ?? nested.hasMore ?? false,
  };
}

function hasValidReceiptUrl(url) {
  const value = String(url || "").trim();
  if (!value) return false;
  if (!/^https?:\/\//i.test(value)) return false;
  return !value.endsWith("/null");
}

function normalizeOrderId(rawOrderId) {
  const normalized = String(rawOrderId || "").trim();
  if (!normalized) return "";
  return normalized.split("?")[0].trim();
}

function getItemDisplayName(item) {
  const name = String(item?.itemName || "").trim();
  return name || "Product";
}

function getItemNameToggleKey(order, orderIndex, item, itemIndex) {
  const orderId = normalizeOrderId(order?.orderId) || `order-${orderIndex}`;
  const itemCode = String(item?.itemCode || "").trim();
  const itemName = getItemDisplayName(item);

  return `${orderId}::${itemCode || itemName}::${itemIndex}`;
}

function isItemNameExpanded(key) {
  return expandedItemNames.value.has(String(key || ""));
}

function registerItemNameElement(key, element) {
  const normalizedKey = String(key || "");
  if (!normalizedKey) return;

  if (element) {
    itemNameElements.set(normalizedKey, element);
  } else {
    itemNameElements.delete(normalizedKey);
  }
}

function recomputeReadMoreEligibility() {
  nextTick(() => {
    const next = new Set();

    for (const [key, element] of itemNameElements.entries()) {
      if (!element) continue;

      const verticalOverflow = element.scrollHeight - element.clientHeight > 1;
      const horizontalOverflow = element.scrollWidth - element.clientWidth > 1;

      if (verticalOverflow || horizontalOverflow) {
        next.add(key);
      }
    }

    readMoreEligibleKeys.value = next;
    expandedItemNames.value = new Set(
      Array.from(expandedItemNames.value).filter((key) => next.has(key)),
    );
  });
}

function shouldShowReadMore(order, orderIndex, item, itemIndex) {
  const key = getItemNameToggleKey(order, orderIndex, item, itemIndex);
  return readMoreEligibleKeys.value.has(key);
}

function toggleItemName(key) {
  const normalizedKey = String(key || "");
  if (!normalizedKey) return;

  const next = new Set(expandedItemNames.value);
  if (next.has(normalizedKey)) {
    next.delete(normalizedKey);
  } else {
    next.add(normalizedKey);
  }
  expandedItemNames.value = next;
}

function isPlacedStatus(status) {
  return (
    String(status || "")
      .trim()
      .toUpperCase() === "PLACED"
  );
}

function isSuccessStatus(status) {
  return (
    String(status || "")
      .trim()
      .toUpperCase() === "SUCCESS"
  );
}

function normalizeRoleToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const showCv = computed(() => {
  try {
    const rawAuth = window.localStorage.getItem("auth.currentUser");
    const authPayload = rawAuth ? JSON.parse(rawAuth) : {};
    const user = authPayload?.data || authPayload || {};
    const userType = normalizeRoleToken(
      user?.userType || user?.role || user?.loginRole || user?.customerType,
    );
    return userType === "DISTRIBUTOR";
  } catch (error) {
    return false;
  }
});

function openAllocateCvPopup(order) {
  try {
    const registerRaw = window.localStorage.getItem("register.api.response");
    const authRaw = window.localStorage.getItem("auth.currentUser");
    const registerPayload = registerRaw ? JSON.parse(registerRaw) : {};
    const authPayload = authRaw ? JSON.parse(authRaw) : {};
    const registerData = registerPayload?.data || registerPayload || {};
    const authUser = authPayload?.data || authPayload || {};

    const baId = String(
      registerData?.distributorCode ||
        authUser?.distributorCode ||
        authUser?.distributorCode,
    ).trim();
    const baName = String(
      registerData?.firstName ||
        registerData?.name ||
        authUser?.firstName ||
        authUser?.name ||
        authUser?.fullName ||
        authUser?.customerName ||
        "",
    ).trim();
    const orderRef = String(
      order?.billId || normalizeOrderId(order?.orderId) || "",
    ).trim();

    if (!baId || !orderRef) {
      errorMessage.value =
        "Unable to open Allocate CV. Missing BA ID or Order Reference.";
      return;
    }

    errorMessage.value = "";
    allocateCvUrl.value = `${BA_URL}/?step=5&baId=${encodeURIComponent(
      baId,
    )}&baName=${encodeURIComponent(baName)}&orderRef=${encodeURIComponent(
      orderRef,
    )}&registrationToken=abc`;

    showAllocateCvPopup.value = true;
  } catch (error) {
    errorMessage.value = "Unable to open Allocate CV.";
  }
}

// ── DELIVERY STATUS ──────────────────────────────────────────
// Delivery Status now lives on its own page (DeliveryStatus.vue) instead of
// a popup. The order object already has everything that page needs, so it's
// stashed in sessionStorage under the order id and read back there — avoids
// re-fetching the order list on a second page.
function goToDeliveryStatus(order) {
  const orderId = normalizeOrderId(order?.orderId);
  if (!orderId) return;

  try {
    window.sessionStorage.setItem(
      `deliveryStatus.order.${orderId}`,
      JSON.stringify(order),
    );
  } catch (error) {
    // sessionStorage can fail (private browsing, quota); DeliveryStatus.vue
    // shows a "couldn't find this order" state in that case.
  }

  router.push({ name: "delivery-status", params: { orderId } });
}

function displayStatus(status) {
  if (isPlacedStatus(status)) {
    return "PENDING PAYMENT";
  }
  return String(status || "-").trim() || "-";
}

function isPaymentSuccessful(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};
  const statusCode = String(data.statusCode || nested.statusCode || "").trim();
  const statusText = String(
    data.status ||
      data.transactionStatus ||
      data.paymentStatus ||
      nested.status ||
      nested.transactionStatus ||
      nested.paymentStatus ||
      "",
  )
    .trim()
    .toLowerCase();

  if (
    statusCode === "200" &&
    ["success", "paid", "completed"].includes(statusText)
  ) {
    return true;
  }
  return ["success", "paid", "completed"].includes(statusText);
}

function resolveVerifyOrderStatus(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};
  const order = data?.order || nested?.order || {};

  const statusCandidate =
    order?.orderStatus ||
    data?.orderStatus ||
    nested?.orderStatus ||
    data?.status ||
    nested?.status ||
    data?.paymentStatus ||
    nested?.paymentStatus ||
    data?.transactionStatus ||
    nested?.transactionStatus ||
    "";

  return String(statusCandidate || "")
    .trim()
    .toUpperCase();
}

function getApiStatusCode(source) {
  const data =
    source?.response?.data || source?.data || source || source?.response || {};
  const nested = data?.data || {};
  return String(data?.statusCode || nested?.statusCode || "").trim();
}

function getApiStatusMessage(source) {
  const data =
    source?.response?.data || source?.data || source || source?.response || {};
  const nested = data?.data || {};
  return String(data?.statusMessage || nested?.statusMessage || "").trim();
}

function getFreechargeFailureMessage(error, fallback = "") {
  return (
    error?.response?.data?.statusMessage ||
    error?.message ||
    fallback ||
    "Failed to initiate Freecharge payment."
  );
}

function isPaymentAlreadyRecordedResponse(source) {
  const statusCode = getApiStatusCode(source);
  const statusMessage = getApiStatusMessage(source).toLowerCase();

  return (
    statusCode === "400" && statusMessage.includes("payment already recorded")
  );
}

function stripWrappedText(value) {
  const wrappers = new Set(['"', "'", "`", "(", ")", "[", "]"]);
  let next = String(value || "").trim();

  while (next.length && wrappers.has(next.charAt(0))) {
    next = next.slice(1).trim();
  }

  while (next.length && wrappers.has(next.charAt(next.length - 1))) {
    next = next.slice(0, -1).trim();
  }

  return next;
}

function normalizeCheckoutUrl(value) {
  const raw = stripWrappedText(value);
  if (!raw) return "";

  const markdownMatch = raw.match(/\((https?:\/\/[^)]+)\)/i);
  const directMatch = raw.match(/https?:\/\/[^\s"')\]]+/i);
  const extracted =
    stripWrappedText(markdownMatch?.[1] || directMatch?.[0] || raw) || "";

  return extracted.replace(/["'`]+$/g, "").trim();
}

function getFreechargeCheckoutPayload(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};

  const checkoutUrl = normalizeCheckoutUrl(
    data.checkoutUrl ||
      nested.checkoutUrl ||
      data.checkoutURL ||
      nested.checkoutURL ||
      data.url ||
      nested.url ||
      "",
  );

  const formDataCandidate =
    data.formData ||
    nested.formData ||
    data.checkoutForm ||
    nested.checkoutForm ||
    null;

  const formData =
    formDataCandidate && typeof formDataCandidate === "object"
      ? Object.entries(formDataCandidate).reduce((acc, [key, value]) => {
          if (!key) return acc;
          acc[key] = String(value ?? "").trim();
          return acc;
        }, {})
      : null;

  return {
    checkoutUrl,
    formData,
    merchantTxnId: String(
      data.merchantTxnId || nested.merchantTxnId || "",
    ).trim(),
  };
}

function submitHostedPaymentForm(checkoutUrl, formData) {
  if (typeof document === "undefined") {
    throw new Error("Unable to open payment checkout outside browser context.");
  }

  const actionUrl = normalizeCheckoutUrl(checkoutUrl);
  if (!actionUrl) {
    throw new Error("Checkout URL is missing in payment response.");
  }

  if (!formData || typeof formData !== "object") {
    throw new Error("Checkout form data is missing in payment response.");
  }

  const entries = Object.entries(formData).filter(([key]) =>
    String(key).trim(),
  );
  if (!entries.length) {
    throw new Error("Checkout form data is empty in payment response.");
  }

  const form = document.createElement("form");
  form.method = "POST";
  form.action = actionUrl;
  form.style.display = "none";

  for (const [key, value] of entries) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = String(key);
    input.value = String(value ?? "");
    form.appendChild(input);
  }

  (document.body || document.documentElement).appendChild(form);
  form.submit();
}

function getHdfcRedirectUrl(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};

  const normalizeCandidateUrl = (value) => {
    const next = stripWrappedText(value);
    if (!next) return "";

    return next;
  };

  const candidates = [
    data.paymentLink,
    nested.paymentLink,
    data.paymentUrl,
    data.paymentURL,
    data.returnUrl,
    data.returnURL,
    data.redirectUrl,
    data.redirectURL,
    data.url,
    data.gatewayUrl,
    data.gatewayURL,
    data.hdfcUrl,
    nested.paymentUrl,
    nested.paymentURL,
    nested.returnUrl,
    nested.returnURL,
    nested.redirectUrl,
    nested.redirectURL,
    nested.url,
    nested.gatewayUrl,
    nested.gatewayURL,
    nested.hdfcUrl,
  ];

  return (
    candidates
      .map(normalizeCandidateUrl)
      .find((entry) => /^https?:\/\//i.test(entry)) || ""
  );
}

function extractUidFromUrlLike(value) {
  const raw = stripWrappedText(value);
  if (!raw) return "";

  const candidates = [raw];
  try {
    candidates.push(decodeURIComponent(raw));
  } catch (error) {
    // ignore decode failures
  }

  for (const candidate of candidates) {
    try {
      const parsed = new URL(candidate);
      const uid = stripWrappedText(
        parsed.searchParams.get("uid") ||
          parsed.searchParams.get("UID") ||
          parsed.searchParams.get("paymentUid") ||
          parsed.searchParams.get("paymentUID") ||
          "",
      );
      if (uid) return uid;
    } catch (error) {
      const qIndex = candidate.indexOf("?");
      if (qIndex < 0) {
        continue;
      }

      const params = new URLSearchParams(candidate.slice(qIndex + 1));
      const uid = stripWrappedText(
        params.get("uid") ||
          params.get("UID") ||
          params.get("paymentUid") ||
          params.get("paymentUID") ||
          "",
      );
      if (uid) return uid;
    }
  }

  return "";
}

function getHdfcUid(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};

  const fromBody = stripWrappedText(
    data.uid ||
      nested.uid ||
      data.paymentUid ||
      nested.paymentUid ||
      data.referenceId ||
      nested.referenceId ||
      "",
  );
  if (fromBody) return fromBody;

  return extractUidFromUrlLike(data.returnUrl || data.returnURL || "") || "";
}

function saveHdfcContext(context) {
  try {
    window.localStorage.setItem(
      HDFC_CONTEXT_STORAGE_KEY,
      JSON.stringify(context || {}),
    );
  } catch (error) {
    // no-op
  }
}

async function redirectToPaymentStatus(response, fallbackOrderId = "") {
  const data = response?.data || response || {};
  const nested = data?.data || {};
  const uid = getHdfcUid(response);
  if (!uid) return false;

  const orderIdFromResponse = normalizeOrderId(
    data.orderId || nested.orderId || fallbackOrderId,
  );

  saveHdfcContext({
    orderId: orderIdFromResponse,
    uid,
    returnUrl: getHdfcRedirectUrl(response),
    source: "order-status-payment-already-recorded",
    createdAt: new Date().toISOString(),
  });

  await router.push({
    name: "payment-status",
    params: { uid },
    query: orderIdFromResponse ? { orderId: orderIdFromResponse } : {},
  });

  return true;
}

function resolveOrderPaymentUid(order) {
  const savedContext = (() => {
    try {
      const raw = window.localStorage.getItem(HDFC_CONTEXT_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  })();

  const currentOrderId = normalizeOrderId(order?.orderId);
  const contextOrderId = normalizeOrderId(savedContext?.orderId);

  const uidCandidates = [
    order?.uid,
    order?.paymentUid,
    order?.paymentUID,
    order?.merchantTxnId,
    order?.merchantTransactionId,
    order?.txnUid,
    currentOrderId && contextOrderId && currentOrderId === contextOrderId
      ? savedContext?.uid
      : "",
    order?.referenceId,
    order?.transactionReferenceId,
  ]
    .map((entry) => stripWrappedText(entry || ""))
    .filter(Boolean);

  // Prefer token-like values (for example MTc4NzI0...) over numeric order-like ids.
  const tokenLikeUid = uidCandidates.find((entry) => /[A-Za-z]/.test(entry));
  if (tokenLikeUid) return tokenLikeUid;

  const nonOrderLikeUid = uidCandidates.find(
    (entry) =>
      entry !== currentOrderId &&
      entry !== String(order?.billId || "").trim() &&
      !/^\d+$/.test(entry),
  );
  if (nonOrderLikeUid) return nonOrderLikeUid;

  return "";
}

function closePaymentRetryFlow() {
  showPaymentFailurePopup.value = false;
  showRetryPaymentOptionsPopup.value = false;
  retryPaymentOrderId.value = "";
  retryPaymentUid.value = "";
  retryPaymentGateway.value = "hdfc";
  isRetryPaymentProcessing.value = false;
}

function openPaymentFailurePopup(orderId, uid = "") {
  retryPaymentOrderId.value = normalizeOrderId(orderId);
  retryPaymentUid.value = stripWrappedText(uid || "");
  retryPaymentGateway.value = "hdfc";
  showRetryPaymentOptionsPopup.value = false;
  showPaymentFailurePopup.value = true;
}

function openRetryPaymentOptionsPopup() {
  showPaymentFailurePopup.value = false;
  showRetryPaymentOptionsPopup.value = true;
}

async function retryWithHdfc(orderId) {
  if (typeof initiateHdfcGatewayPayment !== "function") {
    throw new Error("HDFC payment initiation is not available in the SDK.");
  }

  const response = await initiateHdfcGatewayPayment(orderId);
  const statusCode = getApiStatusCode(response);
  if (statusCode && statusCode !== "200") {
    if (isPaymentAlreadyRecordedResponse(response)) {
      const redirected = await redirectToPaymentStatus(response, orderId);
      if (redirected) {
        return;
      }
    }

    throw new Error(
      getApiStatusMessage(response) || "Failed to initiate HDFC payment.",
    );
  }

  const hdfcUid = getHdfcUid(response) || retryPaymentUid.value;
  const redirectUrl = getHdfcRedirectUrl(response);

  saveHdfcContext({
    orderId,
    uid: hdfcUid,
    returnUrl: redirectUrl,
    source: "order-status-retry-hdfc",
    createdAt: new Date().toISOString(),
  });

  if (!redirectUrl) {
    const redirected = await redirectToPaymentStatus(response, orderId);
    if (redirected) {
      return;
    }

    throw new Error(
      "HDFC returnUrl is missing in payment initiation response.",
    );
  }

  window.localStorage.setItem(PENDING_PAYMENT_ORDER_KEY, orderId);
  window.location.assign(redirectUrl);
}

async function retryWithFreecharge(orderId) {
  if (typeof initiateFreechargeGatewayPayment !== "function") {
    throw new Error(
      "Freecharge payment initiation is not available in the SDK.",
    );
  }

  const response = await initiateFreechargeGatewayPayment(orderId);
  const statusCode = getApiStatusCode(response);
  if (statusCode && statusCode !== "200") {
    throw new Error(
      getApiStatusMessage(response) || "Failed to initiate Freecharge payment.",
    );
  }

  const { checkoutUrl, formData, merchantTxnId } =
    getFreechargeCheckoutPayload(response);
  const uid = stripWrappedText(merchantTxnId || retryPaymentUid.value || "");

  saveHdfcContext({
    orderId,
    uid,
    returnUrl: checkoutUrl,
    source: "order-status-retry-freecharge",
    createdAt: new Date().toISOString(),
  });

  window.localStorage.setItem(PENDING_PAYMENT_ORDER_KEY, orderId);
  submitHostedPaymentForm(checkoutUrl, formData);
}

async function handleRetryPayment() {
  if (isRetryPaymentProcessing.value) return;

  const orderId = normalizeOrderId(retryPaymentOrderId.value);
  if (!orderId) return;

  isRetryPaymentProcessing.value = true;
  paymentActionError.value = "";
  paymentActionErrorOrderId.value = "";

  try {
    if (retryPaymentGateway.value === "freecharge") {
      await retryWithFreecharge(orderId);
      return;
    }

    await retryWithHdfc(orderId);
  } catch (error) {
    const failureMessage = getFreechargeFailureMessage(
      error,
      "Failed to initiate payment.",
    );

    paymentActionError.value = failureMessage;
    paymentActionErrorOrderId.value = orderId;
  } finally {
    isRetryPaymentProcessing.value = false;
  }
}

function canContinueToPayment(order) {
  return (
    Boolean(normalizeOrderId(order?.orderId)) &&
    isPlacedStatus(order?.orderStatus)
  );
}

async function checkPaymentStatusAndContinue(order) {
  const orderId = normalizeOrderId(order?.orderId);
  if (!orderId || processingOrderId.value) return;

  processingOrderId.value = orderId;
  paymentActionError.value = "";
  paymentActionErrorOrderId.value = "";

  try {
    const orderUid = resolveOrderPaymentUid(order);
    if (!orderUid) {
      throw new Error("Payment UID is missing for status verification.");
    }

    if (typeof verifyPaymentStatus !== "function") {
      throw new Error("verifyStatus API is not available in the SDK.");
    }

    const verifyResponse = await verifyPaymentStatus(orderUid);
    const verifyOrderStatus = resolveVerifyOrderStatus(verifyResponse);
    const responseUid = orderUid || getHdfcUid(verifyResponse);

    if (verifyOrderStatus === "PLACED") {
      openPaymentFailurePopup(orderId, responseUid);
      return;
    }

    const redirectUrl = getHdfcRedirectUrl(verifyResponse);
    saveHdfcContext({
      orderId,
      uid: responseUid,
      returnUrl: redirectUrl,
      source: "order-status-check-payment-status",
      createdAt: new Date().toISOString(),
    });

    await router.push({
      name: "payment-status",
      params: responseUid ? { uid: responseUid } : {},
      query: {
        ...(orderId ? { orderId } : {}),
        ...(responseUid ? { uid: responseUid } : {}),
      },
    });
  } catch (error) {
    paymentActionError.value =
      error?.response?.data?.statusMessage ||
      error?.message ||
      "Failed to verify payment status.";
    paymentActionErrorOrderId.value = orderId;
  } finally {
    if (processingOrderId.value === orderId) {
      processingOrderId.value = "";
    }
  }
}

async function maybeNavigateToPaymentSuccess() {
  const orderIdFromQuery = normalizeOrderId(route.query?.orderId);
  const pendingOrderId = normalizeOrderId(
    window.localStorage.getItem(PENDING_PAYMENT_ORDER_KEY),
  );
  const targetOrderId = orderIdFromQuery || pendingOrderId;

  if (!targetOrderId) return false;

  try {
    const response = await checkTransactionStatus(targetOrderId);
    if (!isPaymentSuccessful(response)) return false;

    window.localStorage.removeItem(PENDING_PAYMENT_ORDER_KEY);
    await router.push({
      name: "home",
      query: {
        orderId: targetOrderId,
      },
    });
    return true;
  } catch (error) {
    return false;
  }
}

// ── FETCH ─────────────────────────────────────────────────────
async function fetchOrders({ append = false } = {}) {
  if (append) {
    if (!hasMore.value || isLoadingMore.value || isLoading.value) return;
    isLoadingMore.value = true;
  } else {
    isLoading.value = true;
    nextCursorId.value = null;
    hasMore.value = true;
    bottomLoadTriggered.value = false;
  }
  errorMessage.value = "";
  try {
    const statuses = activeFilter.value === "all" ? [] : [activeFilter.value];
    const response = append
      ? await getOrderList(statuses, nextCursorId.value)
      : await getOrderList(statuses);
    const normalizedOrders = normalizeOrders(response);
    orders.value = append
      ? [...orders.value, ...normalizedOrders]
      : normalizedOrders;
    const pagination = getOrderPagination(response);
    nextCursorId.value = pagination.nextCursorId;
    hasMore.value = Boolean(pagination.hasMore);
    if (activeFilter.value === "all") {
      availableStatuses.value = Array.from(
        new Set(
          normalizedOrders
            .map((order) =>
              String(order.orderStatus || "")
                .trim()
                .toUpperCase(),
            )
            .filter(Boolean),
        ),
      );
    }
    recomputeReadMoreEligibility();
  } catch (error) {
    if (!append) orders.value = [];
    errorMessage.value = error?.message || "Failed to load your orders.";
  } finally {
    if (append) {
      isLoadingMore.value = false;
    } else {
      isLoading.value = false;
    }
  }
}

function onWindowScroll() {
  const scrollBottom = window.innerHeight + window.scrollY;
  const isNearBottom =
    scrollBottom >= document.documentElement.scrollHeight - 200;

  if (!isNearBottom) {
    bottomLoadTriggered.value = false;
    return;
  }

  if (!bottomLoadTriggered.value) {
    bottomLoadTriggered.value = true;
    fetchOrders({ append: true });
  }
}

// ── FILTERS ───────────────────────────────────────────────────
const filters = computed(() => {
  return [
    { value: "all", label: "All" },
    ...availableStatuses.value.map((status) => ({
      value: status,
      label: displayStatus(status),
    })),
  ];
});

const filteredOrders = computed(() => {
  if (activeFilter.value === "all") return orders.value;
  return orders.value.filter(
    (o) =>
      String(o.orderStatus || "")
        .trim()
        .toUpperCase() === activeFilter.value,
  );
});

async function selectFilter(status) {
  const normalizedStatus = String(status || "all")
    .trim()
    .toUpperCase();
  const nextStatus = normalizedStatus === "ALL" ? "all" : normalizedStatus;
  activeFilter.value = nextStatus;
  await fetchOrders();
}

// ── STATUS PILL ───────────────────────────────────────────────
function statusPillClass(status) {
  const s = String(status || "").toUpperCase();
  if (["SUCCESS", "DELIVERED", "COMPLETED", "COMPLETE"].includes(s))
    return "sp-delivered";
  if (["SHIPPED", "IN_TRANSIT", "DISPATCHED", "OUT_FOR_DELIVERY"].includes(s))
    return "sp-transit";
  return "sp-placed";
}

// ── FORMAT ────────────────────────────────────────────────────
function formatCurrency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(amount)}`;
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

onMounted(async () => {
  window.addEventListener("resize", recomputeReadMoreEligibility);
  window.addEventListener("scroll", onWindowScroll, { passive: true });
  const navigatedToSuccess = await maybeNavigateToPaymentSuccess();
  if (navigatedToSuccess) return;
  await fetchOrders();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", recomputeReadMoreEligibility);
  window.removeEventListener("scroll", onWindowScroll);
});

watch(filteredOrders, () => {
  recomputeReadMoreEligibility();
});
</script>

<style scoped>
@import url("https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css");

.orders-page {
  min-height: 100vh;
  background: #f0f4f9;
}
.container {
  width: 92%;
  max-width: 760px;
  margin: 0 auto;
}
.orders-content {
  padding: 36px 0 72px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* PAGE HEAD */
.page-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.page-head-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.page-title {
  font-size: 24px;
  font-weight: 800;
  color: #0a2240;
  margin: 0;
}
.page-sub {
  font-size: 13px;
  color: #5a6b82;
  margin: 0;
}

/* FILTERS */
.filter-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.filter-chip {
  text-transform: uppercase;
  border: 1.5px solid #d1dbe8;
  border-radius: 999px;
  padding: 7px 18px;
  font-size: 12px;
  font-weight: 700;
  color: #5a6b82;
  background: #fff;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s;
  letter-spacing: 0.3px;
}
.filter-chip:hover {
  border-color: #003da5;
  color: #003da5;
}
.filter-chip.active {
  border-color: #003da5;
  background: #003da5;
  color: #fff;
  box-shadow: 0 4px 12px rgba(0, 61, 165, 0.2);
}

/* ORDER CARD */
.orders-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.order-card {
  background: #fff;
  border: 1px solid rgba(0, 61, 165, 0.08);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 61, 165, 0.04);
  transition: box-shadow 0.2s, transform 0.2s;
}
.order-card:hover {
  box-shadow: 0 8px 28px rgba(0, 61, 165, 0.1);
  transform: translateY(-1px);
}

/* CARD HEADER */
.order-head {
  padding: 18px 22px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(135deg, #fafcff 0%, #f0f4fb 100%);
  border-bottom: 1px solid #edf2f7;
}
.order-id-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  min-width: 0;
}
.order-id-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.order-id {
  font-size: 16px;
  font-weight: 800;
  color: #0a2240;
  letter-spacing: -0.3px;
}
.order-date {
  font-size: 12px;
  color: #5a6b82;
  display: flex;
  align-items: center;
  gap: 5px;
}
.order-date i {
  font-size: 12px;
}

/* AMOUNT COL */
.order-amount-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
}
.order-amount {
  font-size: 18px;
  font-weight: 800;
  color: #003da5;
}
.order-amount-label {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
}

/* STATUS PILLS */
.status-pill {
  font-size: 10px;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
  letter-spacing: 0.5px;
}
.sp-delivered {
  background: #eaf3de;
  color: #3b6d11;
}
.sp-transit {
  background: #dbeafe;
  color: #1d4ed8;
}
.sp-placed {
  background: #fef3c7;
  color: #92400e;
}

/* ITEMS */
.items-area {
  padding: 16px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-bottom: 1px solid #edf2f7;
}
.item-block {
  border: 1px solid #edf2f7;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}
.item-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px;
}
.item-thumb {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  border: 1px solid #c7d2fe;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4f46e5;
  font-size: 18px;
  flex-shrink: 0;
}
.item-info {
  flex: 1;
  min-width: 0;
}
.item-name {
  font-size: 13px;
  font-weight: 700;
  color: #0a2240;
  margin: 0;
}
.item-name-text {
  display: block;
}
.item-name-text--collapsed {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.item-name-text--expanded {
  white-space: normal;
  overflow: visible;
}
.item-read-more-btn {
  border: none;
  background: transparent;
  color: #003da5;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  padding: 2px 0 0;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.item-sub {
  font-size: 11px;
  color: #94a3b8;
  margin: 3px 0 0;
  font-weight: 500;
}
.item-amt {
  font-size: 13px;
  font-weight: 800;
  color: #003da5;
  flex-shrink: 0;
}

/* TRACKING TOGGLE */
.item-tracking-block {
  border-top: 1px dashed #e2e8f0;
}

.tracking-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;
}
.tracking-toggle:hover {
  background: #fafcff;
}

.tracking-toggle-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.tracking-toggle-right {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #5a6b82;
  font-size: 13px;
}

.tracking-icon-dot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 61, 165, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #003da5;
  font-size: 14px;
}
.tracking-label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #003da5;
  text-transform: uppercase;
}
.tracking-stage-hint {
  font-size: 12px;
  font-weight: 600;
  color: #5a6b82;
}
.toggle-chevron {
  font-size: 18px;
  color: #94a3b8;
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}
.toggle-chevron.rotated {
  transform: rotate(180deg);
}

/* TRACK BODY */
.track-body {
  padding: 14px 12px 16px;
  background: #fafcff;
  border-top: 1px solid #edf2f7;
}
.track {
  display: flex;
  align-items: flex-start;
}

.track-step-group {
  display: flex;
  align-items: center;
  flex: 1;
}
.track-step-group:last-child {
  flex: 0 0 auto;
}

.t-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
}
.t-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  transition: all 0.2s;
}
.t-icon-wrap.done {
  background: linear-gradient(135deg, #639922, #4a7a18);
  color: #fff;
  box-shadow: 0 3px 10px rgba(99, 153, 34, 0.3);
}
.t-icon-wrap.active {
  background: linear-gradient(135deg, #003da5, #0051cc);
  color: #fff;
  box-shadow: 0 0 0 5px rgba(0, 61, 165, 0.12), 0 3px 10px rgba(0, 61, 165, 0.3);
}
.t-icon-wrap.idle {
  background: #f0f4f8;
  border: 1.5px solid #d1dbe8;
  color: #cbd5e1;
}

.t-name {
  font-size: 11px;
  font-weight: 700;
  text-align: center;
  max-width: 72px;
}
.t-name.done {
  color: #3b6d11;
}
.t-name.active {
  color: #003da5;
}
.t-name.idle {
  color: #94a3b8;
}

.t-line {
  flex: 1;
  height: 2px;
  background: #e2e8f0;
  margin-bottom: 28px;
  min-width: 24px;
  border-radius: 2px;
}
.t-line.done {
  background: linear-gradient(to right, #639922, #4a7a18);
}

/* FOOTER — receipt + delivery status (left), allocate CV (right) */
.order-footer {
  padding: 12px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  border-top: 1px solid #edf2f7;
}
.order-footer-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.track-delivery-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid rgba(0, 61, 165, 0.2);
  border-radius: 999px;
  background: #fff;
  color: #003da5;
  font-size: 13px;
  font-weight: 700;
  padding: 6px 14px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.18s;
}
.track-delivery-btn:hover {
  background: #003da5;
  color: #fff;
  border-color: #003da5;
}
.receipt-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #003da5;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  border: 1.5px solid rgba(0, 61, 165, 0.2);
  border-radius: 999px;
  padding: 6px 14px;
  transition: all 0.18s;
}
.receipt-link:hover {
  background: #003da5;
  color: #fff;
  border-color: #003da5;
}

.order-action-row {
  padding: 12px 22px 16px;
  border-top: 1px solid #edf2f7;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.allocate-btn {
  height: 46px;
  border-radius: 999px;
  border: none;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #f5b000;
  color: #000;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}

.allocate-btn:hover {
  color: #f5b000;
  background: #003da5;
}

.allocate-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.receipt-allocate-btn {
  height: 30px;
  padding: 0 14px;
  font-size: 13px;
}

.page-allocate-btn {
  min-width: 170px;
}

.pay-now-btn {
  border: none;
  border-radius: 10px;
  height: 42px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #003da5;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.18s, transform 0.18s;
}

.pay-now-btn:hover:not(:disabled) {
  background: #003083;
  transform: translateY(-1px);
}

.pay-now-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.spinning {
  animation: spin 1s linear infinite;
}

.payment-action-error {
  margin: 0;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 600;
}

.payment-popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.56);
  z-index: 2500;
  display: grid;
  place-items: center;
  padding: 16px;
}

.payment-popup-box {
  width: min(460px, 96vw);
  background: #fff;
  border-radius: 14px;
  border: 1px solid #dce7f6;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.3);
  padding: 18px;
  position: relative;
}

.payment-popup-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #d1dbe8;
  background: #fff;
  color: #344760;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.payment-popup-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #0a2240;
}

.payment-popup-sub {
  margin: 8px 0 0;
  font-size: 14px;
  color: #5a6b82;
}

.payment-popup-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.payment-popup-btn {
  border: none;
  border-radius: 999px;
  height: 40px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
}

.payment-popup-btn.ghost {
  background: #fff;
  border: 1.5px solid #d1dbe8;
  color: #344760;
}

.payment-popup-btn.primary {
  background: #003da5;
  color: #fff;
}

.payment-popup-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.retry-options-wrap {
  margin-top: 14px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.retry-option-card {
  border: 1.5px solid #e2eaf6;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: #0a2240;
}

.retry-option-card.active {
  border-color: #003da5;
  background: #fafcff;
}

.cv-popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.56);
  z-index: 2500;
  display: grid;
  place-items: center;
  padding: 16px;
}

.cv-popup-box {
  width: min(1100px, 96vw);
  height: min(86vh, 760px);
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cv-popup-close {
  align-self: flex-end;
  margin: 10px;
  border: 1px solid #0f172a;
  background: transparent;
  color: #0f172a;
  border-radius: 8px;
  height: 36px;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.cv-popup-iframe {
  width: 100%;
  flex: 1;
  border: none;
  background: #ffffff;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* STATES */
.status-text {
  font-size: 14px;
  color: #5a6b82;
  margin: 0;
}
.status-text.error {
  color: #b91c1c;
  font-weight: 700;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 72px 0;
  text-align: center;
}
.empty-icon-wrap {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(0, 61, 165, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #003da5;
}
.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0a2240;
  margin: 0;
}
.empty-sub {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
}

/* RESPONSIVE */
@media (max-width: 600px) {
  .page-head {
    flex-direction: column;
    align-items: stretch;
  }
  .page-allocate-btn {
    width: 100%;
  }
  .order-head {
    padding: 14px 16px;
  }
  .items-area {
    padding: 14px 16px;
  }
  .order-footer {
    padding: 12px 16px;
  }
  .order-action-row {
    padding: 12px 16px 16px;
  }
  .tracking-toggle {
    padding: 12px 16px;
  }
  .track-body {
    padding: 14px 16px 18px;
  }
  .order-amount {
    font-size: 16px;
  }
  .item-name {
    white-space: normal;
  }
}
</style>
